import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { appConfigAPI, authAPI, businessesAPI } from '../services/api';
import { invitesAPI } from '../features/businesses/services/invitesApi';
import ErrorBoundary from '../components/ErrorBoundary';
import { formatApiError } from '../utils/errorMessage';
import AuditLog from '../components/AuditLog';
import PageVisibilityModal from '../components/PageVisibilityModal';
import BusinessLogoManager from '../components/BusinessLogoManager';
import TwoFactorSettings from '../components/TwoFactorSettings';

/**
 * User Settings Page
 * Single Responsibility: Manage user profile and preferences
 */
const UserSettings = ({ selectedBusiness }) => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [showPageVisibilityModal, setShowPageVisibilityModal] = useState(false);
  
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    avatar: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [preferences, setPreferences] = useState({
    default_currency: 'USD',
    date_format: 'MM/DD/YYYY',
    timezone: 'America/New_York',
    email_notifications: true,
    sms_notifications: false,
    reminder_phone: ''
  });
  const [businesses, setBusinesses] = useState([]);
  const [brandingBusinessId, setBrandingBusinessId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);

  useEffect(() => {
    loadUserProfile();
    loadPreferences();
    loadBusinesses();
    loadPendingInvites();
    loadSessions();
  }, []);

  useEffect(() => {
    if (selectedBusiness && businesses.some(b => b.id === Number(selectedBusiness))) {
      setBrandingBusinessId(Number(selectedBusiness));
    }
  }, [selectedBusiness, businesses]);

  const loadUserProfile = async () => {
    try {
      const res = await authAPI.getProfile();
      if (res.data) {
        setProfileData({
          first_name: res.data.first_name || '',
          last_name: res.data.last_name || '',
          email: res.data.email || ''
        });
        // Load preferences from user data if available
        if (res.data.preferences) {
          const userPrefs = typeof res.data.preferences === 'string' 
            ? JSON.parse(res.data.preferences) 
            : res.data.preferences;
          if (userPrefs.default_currency || userPrefs.date_format || userPrefs.timezone) {
            setPreferences(prev => ({
              ...prev,
              default_currency: userPrefs.default_currency || prev.default_currency,
              date_format: userPrefs.date_format || prev.date_format,
              timezone: userPrefs.timezone || prev.timezone,
              email_notifications: userPrefs.email_notifications !== false,
              sms_notifications: userPrefs.sms_notifications === true,
              reminder_phone: userPrefs.reminder_phone || ''
            }));
          }
          if (userPrefs.avatar) {
            setProfileData(prev => ({ ...prev, avatar: userPrefs.avatar }));
          }
        }
      }
    } catch (err) {
      console.error('Failed to load user profile:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
    }
  };

  const loadPreferences = async () => {
    try {
      const res = await appConfigAPI.get();
      if (res.data) {
        setPreferences(prev => ({
          ...prev,
          default_currency: res.data.default_currency || prev.default_currency,
          date_format: res.data.date_format || prev.date_format,
          timezone: res.data.timezone || prev.timezone,
          email_notifications: res.data.email_notifications !== false,
          sms_notifications: res.data.sms_notifications === true
        }));
      }
    } catch (err) {
      console.error('Failed to load preferences:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
    }
  };

  const loadBusinesses = async () => {
    try {
      const res = await businessesAPI.getAll();
      const list = res.data || [];
      setBusinesses(list);
      if (!brandingBusinessId && list.length > 0) {
        setBrandingBusinessId(list[0].id);
      }
    } catch (err) {
      console.error('Failed to load businesses:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
    }
  };

  useEffect(() => {
    if (!businesses.length) return;
    if (selectedBusiness && businesses.some(b => b.id === Number(selectedBusiness))) {
      setBrandingBusinessId((prev) => {
        const targetId = Number(selectedBusiness);
        return prev === targetId ? prev : targetId;
      });
    } else if (brandingBusinessId == null) {
      setBrandingBusinessId(businesses[0].id);
    }
  }, [businesses, selectedBusiness, brandingBusinessId]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authAPI.updateProfile({
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        preferences,
        avatar: profileData.avatar || ''
      });
      
      setSuccess('Profile updated successfully');
      
      // Update user context with new data
      if (updateUser && response.data) {
        updateUser({ 
          ...user, 
          first_name: response.data.first_name,
          last_name: response.data.last_name
        });
      }
      
      // Update local profile data
      setProfileData({
        first_name: response.data.first_name || profileData.first_name,
        last_name: response.data.last_name || profileData.last_name,
        email: response.data.email || profileData.email
      });
    } catch (err) {
      setError(formatApiError(err, 'Failed to update profile'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordData.new_password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      await authAPI.changePassword(
        passwordData.current_password,
        passwordData.new_password
      );
      
      setSuccess('Password changed successfully. Other devices will need to sign in again.');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (err) {
      if (err.response?.data?.code === 'AUTHENTICATION_ERROR') {
        setError('Current password is incorrect');
      } else {
        setError(formatApiError(err, 'Failed to change password'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePreferencesUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await appConfigAPI.update(preferences);
      await authAPI.updateProfile({
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        preferences,
        reminder_phone: preferences.reminder_phone || '',
        avatar: profileData.avatar || ''
      });
      setSuccess('Preferences saved successfully');
    } catch (err) {
      setError(formatApiError(err, 'Failed to save preferences'));
    } finally {
      setLoading(false);
    }
  };

  // Check if user has admin access (platform-level or business-level)
  const hasAdminAccess = () => {
    if (!user) return false;
    
    // Platform-level admins
    if (user.is_superuser || user.role === 'superuser' || user.role === 'admin') {
      return true;
    }
    
    // Note: Business-level admin check would require API call
    // For now, we'll show it to platform admins only
    // Business-level admins (owner, admin, accountant, manager) will be checked by the API
    return false;
  };

  const loadSessions = async () => {
    try {
      const res = await authAPI.getSessions();
      setSessions(res.data || []);
    } catch (err) {
      console.error('Failed to load sessions:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
    }
  };

  const signOutOtherSessions = async () => {
    try {
      await authAPI.revokeOtherSessions();
      await loadSessions();
      setSuccess('Other sessions signed out');
    } catch (err) {
      setError(formatApiError(err, 'Failed to sign out other sessions'));
    }
  };

  const loadPendingInvites = async () => {
    try {
      setInvitesLoading(true);
      const res = await invitesAPI.pending();
      setPendingInvites(res.data || []);
    } catch {
      setPendingInvites([]);
    } finally {
      setInvitesLoading(false);
    }
  };

  const handleAcceptInvite = async (token) => {
    try {
      setLoading(true);
      setError('');
      await invitesAPI.accept(token);
      setSuccess('Invite accepted. Refresh the page if your business list does not update.');
      await loadPendingInvites();
      window.dispatchEvent(new CustomEvent('businesses-updated'));
    } catch (err) {
      setError(formatApiError(err, 'Failed to accept invite'));
    } finally {
      setLoading(false);
    }
  };

  // Build tabs array based on user permissions
  const getTabs = () => {
    const baseTabs = ['profile', 'invites', 'password', 'security', 'preferences', 'notifications', 'privacy', 'page-visibility'];
    if (hasAdminAccess()) {
      return [...baseTabs, 'audit'];
    }
    return baseTabs;
  };

  const tabs = getTabs();

  return (
    <ErrorBoundary section="User Settings">
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account settings and preferences</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" role="alert">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
            {pendingInvites.length > 0 && (
              <div className="mb-6 bg-indigo-50 border border-indigo-200 rounded p-4">
                <p className="text-sm text-indigo-900 font-medium">
                  You have {pendingInvites.length} pending business invite{pendingInvites.length === 1 ? '' : 's'}.
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab('invites')}
                  className="mt-2 text-sm text-indigo-700 underline"
                >
                  Review invites
                </button>
              </div>
            )}
            <form onSubmit={handleProfileUpdate} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={profileData.first_name}
                  onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={profileData.last_name}
                  onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Avatar URL</label>
                {profileData.avatar && (
                  <img src={profileData.avatar} alt="" className="h-12 w-12 rounded-full mb-2 object-cover" />
                )}
                <input
                  type="url"
                  value={profileData.avatar || ''}
                  onChange={(e) => setProfileData({ ...profileData, avatar: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="https://"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={profileData.email}
                  disabled
                  className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        )}

        {/* Invites Tab */}
        {activeTab === 'invites' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-2">Business Invites</h2>
            <p className="text-sm text-gray-600 mb-4">
              Accept an invite to join a business. Division-scoped invites only grant access to assigned DBA/division
              operations (Transactions &amp; Budgets).
            </p>
            {invitesLoading ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : pendingInvites.length === 0 ? (
              <p className="text-sm text-gray-500">No pending invites.</p>
            ) : (
              <ul className="divide-y border rounded">
                {pendingInvites.map((inv) => (
                  <li key={inv.id || inv.invite_token} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="font-medium">{inv.business_name || `Business #${inv.business_id}`}</div>
                      <div className="text-sm text-gray-500">
                        Role: {inv.member_role}
                        {Array.isArray(inv.state_registration_ids) && inv.state_registration_ids.length > 0
                          ? ` · Division-scoped (${inv.state_registration_ids.length})`
                          : ' · Full business access'}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => handleAcceptInvite(inv.invite_token)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Accept
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Change Password</h2>
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password *</label>
                <input
                  type="password"
                  required
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
                <input
                  type="password"
                  required
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password *</label>
                <input
                  type="password"
                  required
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            <TwoFactorSettings />
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Current sessions</h2>
                <button type="button" onClick={signOutOtherSessions} className="px-3 py-2 border rounded text-sm">
                  Sign out other sessions
                </button>
              </div>
              {sessions.length === 0 ? (
                <p className="text-sm text-gray-600">No current sessions.</p>
              ) : (
                <ul className="divide-y">
                  {sessions.map((session) => (
                    <li key={session.id} className="py-2 text-sm text-gray-700">
                      {session.current ? 'This session' : 'Other session'} · {session.ip_address || 'Unknown IP'} · {session.user_agent || 'Unknown device'}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Preferences</h2>
            <form onSubmit={handlePreferencesUpdate} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Currency</label>
                <select
                  value={preferences.default_currency}
                  onChange={(e) => setPreferences({ ...preferences, default_currency: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date Format</label>
                <select
                  value={preferences.date_format}
                  onChange={(e) => setPreferences({ ...preferences, date_format: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                <select
                  value={preferences.timezone}
                  onChange={(e) => setPreferences({ ...preferences, timezone: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
              >
                {loading ? 'Saving...' : 'Save Preferences'}
              </button>
            </form>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Notification Preferences</h2>
            <form onSubmit={handlePreferencesUpdate} className="space-y-4 max-w-md">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={preferences.email_notifications}
                  onChange={(e) => setPreferences({ ...preferences, email_notifications: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm text-gray-700">Email Notifications</span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={preferences.sms_notifications}
                  onChange={(e) => setPreferences({ ...preferences, sms_notifications: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm text-gray-700">SMS Notifications</span>
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reminder phone</label>
                <input
                  type="tel"
                  value={preferences.reminder_phone || ''}
                  onChange={(e) => setPreferences({ ...preferences, reminder_phone: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Phone number for reminders"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
              >
                {loading ? 'Saving...' : 'Save Preferences'}
              </button>
            </form>
          </div>
        )}

        {/* Audit Tab - Admin Only */}
        {activeTab === 'privacy' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Privacy</h2>
            <p className="text-sm text-gray-700 mb-4">
              You can download accounts, transactions, budgets, bills, and assets for locations you can open.
            </p>
            <p className="text-sm text-gray-700 mb-4">
              Closing an account marks that account inactive. It does not erase the books.
            </p>
            <a href="/legal/privacy-policy" className="text-blue-600 underline">Open the privacy policy</a>
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="bg-white rounded-lg shadow p-6">
            <AuditLog />
          </div>
        )}

        {/* Page Visibility Tab */}
        {activeTab === 'page-visibility' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-4">
              <h2 className="text-xl font-semibold mb-2">Page Visibility</h2>
              <p className="text-gray-600 text-sm mb-4">
                Control which pages are visible to you and your subordinate users. 
                This helps customize the interface based on user roles and responsibilities.
              </p>
              <p className="text-xs text-gray-500 mb-4">
                Hidden pages remain listed here, so you can always re-enable them later.
              </p>
              <button
                onClick={() => setShowPageVisibilityModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Manage Page Visibility
              </button>
            </div>

            <div className="border-t border-gray-200 pt-6 mt-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-semibold">Business Logo</h3>
                  <p className="text-sm text-gray-600">
                    Upload your logo once and it will show in Business Management, the header, and invoice PDFs.
                  </p>
                </div>
                {businesses.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Choose Business</label>
                    <select
                      value={brandingBusinessId || ''}
                      onChange={(e) => setBrandingBusinessId(e.target.value ? Number(e.target.value) : null)}
                      className="border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      {businesses.map(b => (
                        <option key={b.id} value={b.id}>{b.name || b.legal_name || `Business ${b.id}`}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {businesses.length === 0 ? (
                <p className="text-sm text-gray-500">
                  You need at least one business to upload a logo. Create a business from the Business page first.
                </p>
              ) : (
                <BusinessLogoManager
                  key={brandingBusinessId || 'none'}
                  businessId={brandingBusinessId || businesses[0]?.id}
                  onChange={loadBusinesses}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Page Visibility Modal */}
      <PageVisibilityModal
        isOpen={showPageVisibilityModal}
        onClose={() => setShowPageVisibilityModal(false)}
      />
    </ErrorBoundary>
  );
};

export default UserSettings;

